/* import __COLOCATED_TEMPLATE__ from './email.hbs'; */
/* RESPONSIBLE TEAM: team-channels */
import type AppService from 'embercom/services/app-service';
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { type TaskGenerator } from 'ember-concurrency';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { type TabItem } from 'embercom/components/new-settings/common/standard-base';
import type Brand from 'embercom/models/brand';
import type CustomEmailAddress from 'embercom/models/custom-email-address';
import type SenderEmailAddressSettings from 'embercom/models/sender-email-address-settings';
import type BrandService from 'embercom/services/brand-service';
import { type Domain } from 'embercom/services/domain-service';
import type IntlService from 'embercom/services/intl';

interface Args {
  model: $TSFixMe;
  tab: string;
  step: string | null;
  email: string | null;
  changeTab: (tab: string) => void;
  changeStep: (step: string | null, place: string) => void;
  setEmail: (email: string | null) => Promise<void>;
  domains: Domain[];
  setDomainName: (domainName: string | null) => void;
  domain: Domain | null;
  previousStepForDomainSetup: string | null;
  setPreviousStepForDomainSetup: (step: string | null) => void;
}

const CAN_MANAGE_MESSAGES_SETTINGS = 'can_manage_messages_settings';

export default class Email extends Component<Args> {
  @service declare appService: AppService;
  @service declare permissionsService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare intercomEventService: $TSFixMe;
  @service declare brandService: BrandService;

  @tracked isAddressSidebarOpen = false;
  @tracked isAddressSidebarInUpdateMode = false;
  @tracked UNVERIFIED_LIMIT = 10;
  @tracked brands: Brand[] = [];
  @tracked senderEmailAddressSettings: SenderEmailAddressSettings[] = [];

  get shouldShowNewEmailAddressButtonInHeader() {
    return this.args.domains.some((domain) => domain.addresses.length > 0);
  }

  get tabs(): Array<TabItem & { canAccess: boolean }> {
    return [
      {
        label: 'new-settings.channels.email.tabs.domains-and-addresses',
        value: 'domains-and-addresses',
        canAccess: true,
      },
      {
        label: 'new-settings.channels.email.tabs.email-settings',
        value: 'email-settings',
        canAccess: true,
      },
    ];
  }

  get filteredTabs() {
    if (this.appService.app.isStandaloneApp) {
      return [];
    }
    return this.tabs.filter((tab) => !!tab.canAccess);
  }

  get domainsAndAddressesLearnDropdownItems() {
    return [
      {
        text: this.intl.t('new-settings.channels.email.learn-menu-items.connect-to-email'),
        icon: 'article',
        onSelectItem: () => this.openArticle(9744849, 'connect_to_email'),
      },
      {
        text: this.intl.t('new-settings.channels.email.learn-menu-items.fin-ai-over-email'),
        icon: 'article',
        onSelectItem: () => this.openArticle(9356221, 'fin_ai_over_email'),
      },
      {
        text: this.intl.t('new-settings.channels.email.learn-menu-items.outbound-emails'),
        icon: 'article',
        onSelectItem: () => this.openArticle(3292845, 'outbound_emails'),
      },
    ];
  }

  get selectedTab() {
    if (this.filteredTabs.find((tab) => tab.value === this.args.tab)) {
      return this.args.tab;
    } else {
      return this.filteredTabs[0]?.value;
    }
  }

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    taskFor(this.brandsLoader).perform();
    taskFor(this.senderEmailAddressSettingsLoader).perform();
    if (!this.args.tab || !this.filteredTabs.find((tab) => tab.value === this.args.tab)) {
      this.setSelectedTab(this.filteredTabs[0]?.value);
    }
  }

  get isLoadBrandsRunning() {
    return taskFor(this.brandsLoader).isRunning;
  }

  get isLoadSenderEmailAddressSettingsRunning() {
    return taskFor(this.senderEmailAddressSettingsLoader).isRunning;
  }

  @dropTask
  *senderEmailAddressSettingsLoader(): TaskGenerator<SenderEmailAddressSettings[]> {
    this.senderEmailAddressSettings = yield this.store.findAll('sender-email-address-settings');
    return this.senderEmailAddressSettings;
  }

  @dropTask
  *brandsLoader(): TaskGenerator<Brand[]> {
    if (!this.brandService.isAppOnRightMultibrandPlan) {
      return [];
    }

    try {
      this.brands = yield this.store.findAll('brand');
      return this.brands;
    } catch (error) {
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('channels.email.settings.signature.load-error'),
      });
      return [];
    }
  }

  get canSetupEmailForwarding() {
    return this.permissionsService.currentAdminCan(CAN_MANAGE_MESSAGES_SETTINGS);
  }

  get app() {
    return this.appService.app;
  }

  get customBounceSettings() {
    return this.app.customBounceSettings;
  }

  get dkimSettings() {
    return this.app.dkim_settings;
  }

  get customEmailAddresses() {
    return this.app.customEmailAddresses;
  }

  get totalUnverifiedEmailsLimit() {
    if (!this.app.customEmailAddresses) {
      return false;
    }
    let unverifiedEmailsCount = this.app.customEmailAddresses.filter(
      (email: CustomEmailAddress) => !email.verified,
    ).length;
    return unverifiedEmailsCount >= this.UNVERIFIED_LIMIT;
  }

  @action
  setSelectedTab(value: string): void {
    this.args.changeTab(value);
    this.intercomEventService.trackAnalyticsEvent({
      action: 'viewed',
      object: `${value}-tab`,
      place: 'apps.app.settings.channels.email',
      section: 'ia-settings',
      context: `new-settings.channels.email.tabs.${value}`,
    });
  }

  @action
  openAddressSidebar(updatingExistingEmail: boolean) {
    this.isAddressSidebarOpen = true;
    this.isAddressSidebarInUpdateMode = updatingExistingEmail;

    this.intercomEventService.trackAnalyticsEvent({
      action: 'opened',
      object: 'address_sidebar',
      place: 'apps.app.settings.channels.email',
      updateMode: updatingExistingEmail,
    });
  }

  @action
  closeAddressSidebar() {
    this.isAddressSidebarOpen = false;
  }

  @action
  openArticle(id: number, name: string) {
    window.Intercom('showArticle', id);
    this.intercomEventService.trackAnalyticsEvent({
      action: 'opened',
      object: `learn_about_${name}`,
      place: 'settings_email_channels',
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Channels::Email': typeof Email;
  }
}
